import "./ClinicalFormDetailsView.css";
import 'react-quill/dist/quill.snow.css';

import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from "react";
import {
  ClinicalFormDetails,
  FormMessage,
  MemberAnswerInfo,
  QuestionInfo,
} from "clinical-forms-experience-client";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import ClinicalFormQuestionView from "./ClinicalFormQuestionView";
import ClinicalFormQuestionTableView from "./ClinicalFormQuestionTableView";
import AssessmentProgress from "./AssessmentProgress";
import ReactQuill from "react-quill";
import FormMessageView from "./FormMessageView";
import { ClinicalFormsConstants } from "./ClinicalFormsConstants";

const modules = {
  toolbar: [
    [{ size: [] }, 'bold', 'italic', 'underline', { 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }, 'clean']
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  }
};

// Flatten the question tree (including conditional dependentQuestions nested under options)
// into an id -> question map, so the completion gate can look up isRequired by id.
const flattenQuestions = (
  questions: QuestionInfo[]
): Map<string, QuestionInfo> => {
  const byId = new Map<string, QuestionInfo>();
  const visit = (qs: QuestionInfo[]) => {
    qs.forEach((q) => {
      byId.set(q.id, q);
      q.options?.forEach((o) => o.dependentQuestions && visit(o.dependentQuestions));
    });
  };
  visit(questions);
  return byId;
};

interface Props {
  children?: ReactElement,
  formId: string;
  answers?: MemberAnswerInfo[];
  comments?: string;
  commentsPosition?: "bottom" | "right";
  readOnly: boolean;
  title?: any;
  commentsTitle?: string;
  hideComments?: boolean;
  height?: string;

  onAnswersChange?: (questionId: string, answer: MemberAnswerInfo) => void;
  onAllAnswersGivenChange?: (allAnswersGiven: boolean) => void;
  onCommentsChange?: (comments: string) => void;
  onHasQuestionsChange?: (hasQuestions: boolean) => void;
}

const ClinicalFormDetailsView: React.FC<Props> = ({
  children,
  formId,
  answers,
  comments,
  commentsPosition = "bottom",
  readOnly,
  title,
  commentsTitle,
  hideComments,
  height,
  onAnswersChange,
  onAllAnswersGivenChange,
  onCommentsChange,
  onHasQuestionsChange,
}: Props) => {
  const clinicalformsApi = createClinicalFormsApi();

  const [clinicalFormDetails, setClinicalFormDetails] =
    useState<ClinicalFormDetails | null>(null);

  const [memberAnswers, setMemberAnswers] = useState<Map<string, MemberAnswerInfo>>(
    new Map()
  );

  const [optionMessageIdsMap, setOptionMessageIdsMap] = useState<Map<string, Set<string>>>(new Map());
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [topPrioritizedBanner, setTopPrioritizedBanner] = useState<FormMessage>();

  const [questionsVisibleIds, setQuestionsVisibleIds] = useState<Set<string>>(new Set());
  const [answersRemainingCount, setAnswersRemainingCount] = useState(0);
  const [allRequiredAnswered, setAllRequiredAnswered] = useState(false);

  const [questionsHiddenConditionaly, setQuestionsHiddenConditionaly] = useState(new Set<string>());
  const [hiddenQuestionIds, setHiddenQuestionIds] = useState(new Set<string>());

  const [clinicalFormTitle , setClinicalFormTitle] = useState<string | null>(null);


  useEffect(() => {
    if (!clinicalFormDetails) {
      return;
    }

    let remainingCount = findAnswerLeft();
    setAnswersRemainingCount(remainingCount);
    // Submission is gated only on required questions.
    const requiredLeft = findRequiredAnswersLeft();
    setAllRequiredAnswered(requiredLeft == 0);
    onAllAnswersGivenChange?.(requiredLeft == 0);

    conditionalyHideQuestions();

    optionMessageIdsMap.clear();
    createOptionMessageIdsMap(clinicalFormDetails.questions);
  }, [clinicalFormDetails, memberAnswers, questionsVisibleIds]);

  const createOptionMessageIdsMap = (questions: QuestionInfo[]) => {
    questions.forEach(question => {
      question.options?.forEach(option => {
        if (option.messageIds!.length > 0) {
          optionMessageIdsMap.set(option.id, new Set(option.messageIds));
        }
        if (option.dependentQuestions) {
          createOptionMessageIdsMap(option.dependentQuestions);
        }
      });
    });
    setOptionMessageIdsMap(new Map(optionMessageIdsMap));
  }

  const conditionalyHideQuestions = () => {
    clinicalFormDetails?.questions.forEach(question => {
      if (question.visibilityConditionType && question.visibilityConditionType.length > 0) {
        questionsHiddenConditionaly.add(question.id);
        hiddenQuestionIds.add(question.id);
      } else {
        return;
      }

      if (question.visibilityConditionType === "questions-set-score-sum") {
        const questionIdsForScore = question.visibilityConditionData?.split(",");
        const scoreParts = question.visibilityConditionCriteria!.split("-");
        const minScore = Number(scoreParts[0]);
        const maxScore = scoreParts.length > 0 ? Number(scoreParts[0]) : undefined;

        let score = 0;
        questionIdsForScore?.forEach(questionIdForScore => {
          const questionForScore = clinicalFormDetails?.questions.find(q => q.id === questionIdForScore);
          const answerGiven = memberAnswers?.get(questionIdForScore)?.answer;

          if (answerGiven) {
            questionForScore?.options?.forEach(option => {
              if (answerGiven === option.id) {
                score += option.score ?? 0;
              }
            });
          }
        });
        if (score >= minScore && (!maxScore || maxScore >= minScore)) {
          questionsHiddenConditionaly.delete(question.id);
        }
      }
    });
    setQuestionsHiddenConditionaly(new Set(questionsHiddenConditionaly));
    setHiddenQuestionIds(new Set(hiddenQuestionIds));
  }

  const findAnswerLeft = () => {
    let answersGivenCount = 0;
    Array.from(questionsVisibleIds).forEach(questionId => {
      const memberAnswer = memberAnswers.get(questionId);
      if (memberAnswer?.questionId === questionId && memberAnswer.answer.length > 0) {
        answersGivenCount++;
      }
    });
    return questionsVisibleIds.size - answersGivenCount;
  }

  const findRequiredAnswersLeft = () => {
    const questionsById = flattenQuestions(clinicalFormDetails?.questions ?? []);
    let requiredRemaining = 0;
    Array.from(questionsVisibleIds).forEach(questionId => {
      if (!questionsById.get(questionId)?.isRequired) {
        return;
      }
      const memberAnswer = memberAnswers.get(questionId);
      if (!(memberAnswer?.questionId === questionId && memberAnswer.answer.length > 0)) {
        requiredRemaining++;
      }
    });
    return requiredRemaining;
  }

  useEffect(() => {
    if (!formId) {
      return;
    }

    const loadForm = async () => {
      LoadingHelper.showLoader(".clinical-form-details");

      try {
        // Load clinical form details
        const formResponse = (
          await clinicalformsApi.clinicalFormsControllerGetClinicalForm(formId)
        ).data;
        setQuestionsVisibleIds(new Set());
        setClinicalFormTitle(formResponse.title)
        setAnswersRemainingCount(0);
        setClinicalFormDetails(formResponse);
      } catch (error) {
        console.error("Error loading clinical form:", error);
      } finally {
        LoadingHelper.hideLoader(".clinical-form-details");
      }
    };

    loadForm();
  }, [formId]);

  // Report whether the loaded form has any questions (notes-only forms have none), so a consumer
  // can adapt its layout. Optional — consumers that don't pass the callback are unaffected.
  useEffect(() => {
    if (!clinicalFormDetails) {
      return;
    }
    onHasQuestionsChange?.((clinicalFormDetails.questions?.length ?? 0) > 0);
  }, [clinicalFormDetails]);

  useEffect(() => {
    if (!answers || answers.length === 0) {
      return;
    }

    const memberAnswers = new Map<string, MemberAnswerInfo>();
    answers.forEach((answer) => {
      memberAnswers.set(answer.questionId, answer);
    });
    setMemberAnswers(memberAnswers);
  }, [answers]);

  const onAnswerChange = async (questionId: string, answer: MemberAnswerInfo) => {
    memberAnswers.set(questionId, answer);
    onAnswersChange?.(questionId, answer);

    // clear answers for child questions of non-selected options
    const question = clinicalFormDetails?.questions.find(q => q.id === questionId);
    question?.options?.forEach(option => {
      option.dependentQuestions?.forEach(dependentQuestion => memberAnswers.delete(dependentQuestion.id));
    });

    setMemberAnswers(new Map(memberAnswers));

    conditionalyHideQuestions();
  };

  useEffect(() => {
    selectedMessageIds.clear();
    updateSelectedMessageIds();
  }, [memberAnswers]);

  const updateSelectedMessageIds = () => {
    memberAnswers.forEach((answer) => {
      const messageIds = optionMessageIdsMap.get(answer.answer);
      if (messageIds) {
        messageIds.forEach(messageId => {
          selectedMessageIds.add(messageId);
        });
      }
    });
    setSelectedMessageIds(new Set(selectedMessageIds));

    const topPrioritizedBanners = clinicalFormDetails
      ?.messages
      .filter(message => selectedMessageIds.has(message.id))
      ?.filter(message => message.type === ClinicalFormsConstants.topPrioritizedBanner)
      .sort((a, b) => b.priority - a.priority);
    if (topPrioritizedBanners && topPrioritizedBanners.length > 0) {
      setTopPrioritizedBanner(topPrioritizedBanners[0]);
    } else {
      setTopPrioritizedBanner(undefined);
    }
  }

  const onMount = (id: string) => {
    setQuestionsVisibleIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  const onUnmount = (id: string) => {
    setQuestionsVisibleIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  const messagesView = () => {
    if (!topPrioritizedBanner) {
      return <></>;
    }
    return <>
      <div className="mt-2">
        <FormMessageView key={topPrioritizedBanner.id} message={topPrioritizedBanner} />
      </div>
    </>;
  }

  const questionsTableHeaderView = () => {
    return clinicalFormDetails?.questions
      ?.filter((q) => readOnly ? memberAnswers.has(q.id) : true) // Check if the question ID exists in memberAnswers
      ?.map((question, index) => {
        if (questionsHiddenConditionaly.has(question.id) || index > 0) {
          return;
        }
        return (<thead>
          <tr>
            <th scope="col" className="text-center border" style={{ width: '50%'}}>Question</th>
            {question.options?.map((option) => (
              <th scope="col" key={option.id} className="text-center border">
                <label
                  className="form-check-label"
                >
                  {option.optionText}
                </label>
              </th>
            ))}
          </tr>
        </thead>);
      });
  }

  const questionsView = () => {

    let orderedIndex = -1;

    return clinicalFormDetails?.questions
     ?.filter((q) => readOnly ? memberAnswers.has(q.id) : true) // Check if the question ID exists in memberAnswers
     ?.map((question, index) => {
     if (questionsHiddenConditionaly.has(question.id)) {
      return;
    }

      orderedIndex = ['description', 'collapsible'].includes(question.type) ? orderedIndex : orderedIndex + 1;

       return (
         clinicalFormDetails?.viewMode && clinicalFormDetails?.viewMode === 'table' ?
           <tbody>
             <ClinicalFormQuestionTableView
               key={question.id}
               index={orderedIndex}
               question={question}
               savedAnswers={memberAnswers}
               onAnswerChange={onAnswerChange}
               readOnly={readOnly}
               onMount={onMount}
               onUnmount={onUnmount}
               hiddenQuestionIds={hiddenQuestionIds}
               assessmentId={clinicalFormDetails?.id || '  '}
             />
           </tbody> : <ClinicalFormQuestionView
              key={question.id}
              index={orderedIndex}
              question={question}
              savedAnswers={memberAnswers}
              onAnswerChange={onAnswerChange}
              readOnly={readOnly}
              onMount={onMount}
              onUnmount={onUnmount}
            />
       );
    });
  }

  const commentsView = () => {
    if (readOnly && (!comments || comments.length == 0)) {
      return <></>;
    }
    return <>
      <div className={"clinical-form-comments " + (readOnly ? "bg-teal-lighter " : "") + (commentsPosition == "bottom" ? "mt-3" : "")}>
        <strong className="d-block fs-16 px-4 py-2 border border-bottom-0 rounded-top">{commentsTitle ? `${commentsTitle}` : "Progress Notes"} </strong>
          { readOnly ? (
            <div className="bordered border-top p-4" dangerouslySetInnerHTML={{__html: comments ?? ""}}></div>
          ) :
          ( <ReactQuill
            theme='snow'
            placeholder='Type Notes...'
            style={{
              height: commentsPosition == "right" ? `calc(100vh - 19.25rem)` : height ? height : "100px",
              backgroundColor: 'rgba(239, 239, 239, 0.3)',
              border: '1px solid rgba(118, 118, 118, 0.3)'
            }}
            formats={['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'indent']}
            modules={readOnly ? { toolbar: false } : modules}
            value={comments}
            onChange={(value) => {
              comments = value;
              onCommentsChange?.(value);
            }}
            className={readOnly ? "disabled-ql-editor" : 'bg-white'}
            readOnly={readOnly}
          />)}
        </div>
    </>;
  }

  return (
    <>
        {commentsPosition == "bottom" ? (
        <div className="clinical-form-details">
          {messagesView()}
          {
          clinicalFormDetails?.viewMode && clinicalFormDetails?.viewMode === 'table' ?
          <div className="rounded-3 p-5 bg-gray-FAF">
            <h6>The following questions must be asked to the patient and not their representative.</h6>
            <div className="table-responsive">
              <table className="table border mt-4">
                {questionsTableHeaderView()}
                {questionsView()}
              </table>
            </div>
          </div> : questionsView()
          }
          { !hideComments && commentsView() }
        </div>
        ) : (
          <>
            <div className="assessment__main pt-0">
              <div className="position-sticky pt-4 pb-2 border-bottom" style={{top: 0, backgroundColor:  '#fff'}}>
              <div className="d-flex align-items-center">
                <div className="flex-fill">
                  <div className="text-light fs-11">MODULE</div>
                  <div>{clinicalFormTitle}</div>
                </div>
                <AssessmentProgress
                  answered={questionsVisibleIds.size - answersRemainingCount}
                  total={questionsVisibleIds.size}
                  ready={allRequiredAnswered}
                />
              </div>
              { children}
              { messagesView() }
              </div>
              {
                clinicalFormDetails?.viewMode && clinicalFormDetails?.viewMode === 'table' ?
                <div className="rounded-3 p-5 bg-gray-FAF">
                  <h6>The following questions must be asked to the patient and not their representative.</h6>
                  <div className="table-responsive">
                    <table className="table border mt-4">
                      {questionsTableHeaderView()}
                      {questionsView()}
                    </table>
                  </div>
                </div> :  questionsView()
              }
            </div>
            { !hideComments && <div className="assessment__aside assessment__aside-lg">{commentsView()}</div> }
          </>
        )}
    </>
  );
};

export default ClinicalFormDetailsView;
